/**
 * Formatting helpers for the Portfolio Story page.
 *
 * The Modernist spec renders every figure in monospace with a leading `€ ` and
 * grouped digits (`€ 123,456`), which is not what `Intl`'s `style: 'currency'`
 * produces, so the symbol is placed by hand.
 *
 * Every helper accepts null/undefined and returns the design's placeholder
 * glyphs, so a chapter renders its full geometry before data arrives and never
 * shifts when it does.
 */

const PLACEHOLDER_MONEY = '€ ---,---';
const PLACEHOLDER_PCT = '--.-%';
const PLACEHOLDER_TEXT = '————';

const groupFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const decimalFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * `€ 123,456` — whole euros, grouped.
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function euro(value) {
  if (!Number.isFinite(value)) {
    return PLACEHOLDER_MONEY;
  }

  const sign = value < 0 ? '−' : '';
  return `${sign}€ ${groupFormatter.format(Math.abs(Math.round(value)))}`;
}

/**
 * `€ 1.23` — two decimals, for the per-euro sentence in chapter 06.
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function euroPrecise(value) {
  if (!Number.isFinite(value)) {
    return '€ -.--';
  }

  return `€ ${decimalFormatter.format(value)}`;
}

/**
 * `+12.3%` — always signed, one decimal.
 *
 * @param {number|null|undefined} value
 * @returns {string}
 */
export function pct(value) {
  if (!Number.isFinite(value)) {
    return PLACEHOLDER_PCT;
  }

  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${Math.abs(value).toFixed(1)}%`;
}

/**
 * `12.08.2026` — the footer date format in chapter 08.
 *
 * @param {string|null|undefined} isoDate - `YYYY-MM-DD` or a full ISO timestamp
 * @returns {string}
 */
export function dateDots(isoDate) {
  if (typeof isoDate !== 'string' || isoDate.length < 10) {
    return '--.--.----';
  }

  const [year, month, day] = isoDate.slice(0, 10).split('-');
  return `${day}.${month}.${year}`;
}

/**
 * `Jan 2025` — used to name the best month in chapter 03.
 *
 * @param {string|null|undefined} yearMonth - `YYYY-MM`
 * @returns {string}
 */
export function monthName(yearMonth) {
  if (typeof yearMonth !== 'string' || yearMonth.length < 7) {
    return PLACEHOLDER_TEXT;
  }

  const [year, month] = yearMonth.split('-');
  const date = new Date(Number(year), Number(month) - 1, 1);

  if (Number.isNaN(date.getTime())) {
    return PLACEHOLDER_TEXT;
  }

  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/**
 * Two-digit chapter number: `01`, `09`.
 *
 * @param {number} value
 * @returns {string}
 */
export function twoDigit(value) {
  return String(value).padStart(2, '0');
}

/**
 * Bar width as a percentage of the widest value in a set.
 *
 * Negative and zero values collapse to nothing when measured against a positive
 * maximum, which would render a −30% loss as a longer bar than a +4% gain once
 * the browser clamps it. Instead every bar is measured on absolute magnitude and
 * anything that would vanish gets a visible floor, leaving the sign to the label
 * beside it.
 *
 * @param {number} value
 * @param {number} max - Largest absolute value in the set
 * @param {number} [floor=2] - Minimum visible width, percent
 * @returns {number} Width percentage, 0–100
 */
export function barWidth(value, max, floor = 2) {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) {
    return 0;
  }

  const width = (Math.abs(value) / max) * 100;
  return Math.max(floor, Math.min(100, width));
}

export { PLACEHOLDER_MONEY, PLACEHOLDER_PCT, PLACEHOLDER_TEXT };
